import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import React, { useCallback, useEffect } from 'react';

import { getCaseOwner } from 'services/caseService';
import { closeAllModals, openModal } from 'reducers/modalReducer';
import { Grid } from '@mui/material';
import CustomCircularProgress from 'components/Common/CustomCircularProgress';
import { setAccountCaseInformation, setCaseId } from 'reducers/caseReducer';
import { BrSubTypeFromBrMgmt, BrType } from 'types/BrTypes';

export type CaseOwnerResponseType = {
  accountInformation?: AccountInformation | null;
  partnerInformation?: PartnerInformation | null;
};

export type AccountInformation = {
  accountNumber: string;
  accountName: string;
  brUid: string;
  brType?: BrType;
  brSubType?: BrSubTypeFromBrMgmt;
};

export type BusinessRelationshipLifecycleAndStatus = {
  businessRelationshipLifecycle: BusinessRelationshipLifecycle;
  businessRelationshipStatus: BusinessRelationshipStatus;
};

export enum BusinessRelationshipLifecycle {
  OPERATING = 'OPERATING',
  ONBOARDING = 'ONBOARDING',
  CLOSED = 'CLOSED',
  OFFBOARDING = 'OFFBOARDING',
  PROSPECTING = 'PROSPECTING',
}

export enum BusinessRelationshipStatus {
  DRAFT = 'DRAFT',
  IN_APPROVAL = 'IN_APPROVAL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN',
  DUPLICATE_CHECK_PENDING = 'DUPLICATE_CHECK_PENDING',
}

export type PartnerInformation = {
  partnerUid: string;
};

const styles = {
  spinnerContainer: {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    left: '48%',
    top: '45%',
  },
};

/**
 * Component to handle call received from MSD with the case id and redirect
 */
const CaseRedirect = () => {
  const dispatch = useDispatch();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();

  const getCaseOwnerDetails = useCallback(
    async (id: string) => {
      try {
        const response: CaseOwnerResponseType = await getCaseOwner(id);

        const { accountInformation, partnerInformation } = response;
        if (accountInformation) {
          navigate('/business-relation/edit');
          dispatch(setAccountCaseInformation(accountInformation));
          dispatch(setCaseId(caseId));
        } else if (partnerInformation) {
          navigate(`/partner/edit/${partnerInformation.partnerUid}/core-data`);
          dispatch(setCaseId(caseId));
        }
      } catch (error) {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: 'Error fetching case owner from products and services',
                title: 'Error fetching case owner',
                userErrorMessage: 'An error occurred while fetching the case owner',
              },
              httpError: error,
              handleClose: () => {
                navigate('/');
                dispatch(closeAllModals());
              },
            },
          }),
        );
      }
    },
    [caseId, dispatch, history],
  );

  useEffect(() => {
    if (caseId) {
      // noinspection JSIgnoredPromiseFromCall
      getCaseOwnerDetails(caseId);
    }
  }, [caseId, getCaseOwnerDetails]);

  return (
    <Grid container alignContent="center" sx={styles.spinnerContainer}>
      <Grid item>
        <CustomCircularProgress size={70} />
      </Grid>
    </Grid>
  );
};

export default CaseRedirect;
